import React from "react";
import { trackSimulatorClick } from "../utils/analytics";
import { trackPhoneClick } from "../utils/analytics";
import Seo from "../components/Seo";
import { serviceJsonLd, filArianeJsonLd, faqJsonLd } from "../utils/donneesStructurees";
import { trouverDepartement } from "../data/departements";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Testimonials from "../components/Testimonials";
import { CheckCircle, Phone, MapPin, Award, ExternalLink } from "lucide-react";
import GuidesLies from "../components/GuidesLies";

/**
 * Page départementale. Tout ce qui distingue un département d'un autre vient de
 * src/data/departements.js : aides locales sourcées, contexte de bâti, communes
 * et FAQ propres. Le composant ne fait que la mise en page.
 *
 * Conformité : aucun montant n'est écrit en dur ici. Les montants d'aides
 * locales viennent des données, avec leur source officielle affichée.
 */
const DepartementPage = ({ dept }) => {
  const d = trouverDepartement(dept);
  if (!d) return null;

  const { code, nom, ou, de, intro, contexte, aidesLocales, communes, faq, local } = d;
  const chemin = `/amo-anah-${dept}-${code}`;

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre={`MaPrimeAdapt' ${nom} (${code}) | AMO agréé ANAH`}
        description={`Aides locales et travaux d'adaptation ${ou} (${code}) : dossier MaPrimeAdapt' monté par un AMO agréé ANAH, jusqu'à 70 % des travaux financés.`}
        chemin={chemin}
        donnees={[
          serviceJsonLd({
            nom: `Accompagnement AMO MaPrimeAdapt' ${nom}`,
            description: `Assistance à maîtrise d'ouvrage agréée ANAH pour l'adaptation du logement des seniors et des personnes en situation de handicap ${ou} (${code}).`,
            url: chemin,
            zones: [{ nom, code }],
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "MaPrimeAdapt'", url: "/maprimeadapt" },
            { nom: `${nom} (${code})`, url: chemin },
          ]),
          faqJsonLd(faq),
        ]}
      />

      <Header />

      <main id="contenu">

      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="text-orange-700" size={32} />
              <span className="text-xl font-semibold text-gray-700">
                {nom} ({code})
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              MaPrimeAdapt' {ou} ({code})
            </h1>
            <p className="text-2xl font-semibold text-orange-700 mb-8">
              AMO agréé ANAH — adaptation du logement des seniors
            </p>

            <p className="text-lg text-gray-700 text-left leading-relaxed mb-10">
              {intro}
            </p>

            <Link
              to="/simulateur"
              onClick={() => trackSimulatorClick("departement_hero")}
              className="inline-block bg-orange-700 text-white text-xl font-bold px-12 py-5 rounded-lg hover:bg-orange-800 transition-colors shadow-lg"
            >
              Tester mon éligibilité — {nom}
            </Link>
            <p className="text-sm text-gray-600 mt-4">
              Gratuit, sans engagement, réponse immédiate
            </p>
          </div>
        </div>
      </section>

      {/* Contexte local : la section qui rend chaque page réellement différente */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10">{contexte.titre}</h2>
            <div className="space-y-8">
              {contexte.points.map((p) => (
                <div key={p.titre} className="border-l-4 border-orange-500 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{p.titre}</h3>
                  <p className="text-gray-700 leading-relaxed">{p.texte}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Aides locales, avec leur source officielle */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">
              Les aides mobilisables {ou} en plus de MaPrimeAdapt'
            </h2>
            <p className="text-gray-700 leading-relaxed mb-10">{aidesLocales.preambule}</p>

            <div className="space-y-6">
              {aidesLocales.dispositifs.map((a) => (
                <div key={a.nom} className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-xl font-bold text-orange-700 mb-3">{a.nom}</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">{a.texte}</p>
                  <a
                    href={a.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-gray-600 underline hover:text-orange-700"
                  >
                    Source officielle du dispositif
                    <ExternalLink size={14} />
                  </a>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-orange-50 border border-orange-200 rounded-lg p-6">
              <p className="text-gray-800 leading-relaxed">
                <strong>Rappel sur MaPrimeAdapt' :</strong> l'aide couvre 70 % du montant
                des travaux pour les ressources très modestes et 50 % pour les ressources
                modestes, dans la limite d'un plafond de travaux de 22 000 € HT — soit une
                aide maximale de 15 400 €. Les aides locales ci-dessus viennent s'ajouter à
                cette subvention et réduisent d'autant votre reste à charge.
              </p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                <Link
                  to="/plafonds-ressources-maprimeadapt"
                  className="text-orange-700 underline hover:text-orange-800"
                >
                  Les plafonds de ressources en Île-de-France
                </Link>
                <Link
                  to="/cumul-aides-ma-prime-adapt-idf"
                  className="text-orange-700 underline hover:text-orange-800"
                >
                  Comment cumuler les aides
                </Link>
                <Link
                  to="/aides-complementaires"
                  className="text-orange-700 underline hover:text-orange-800"
                >
                  Des aides pour réduire votre reste à charge
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travaux, avec maillage vers les pages dédiées */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10">Les travaux que nous montons {ou}</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                <h3 className="text-xl font-bold text-orange-700 mb-4">
                  Salle de bain et douche sénior
                </h3>
                <ul className="space-y-2 mb-4">
                  {[
                    "Douche de plain-pied antidérapante",
                    "Barres d'appui et siège de douche",
                    "WC rehaussé et lavabo adapté",
                  ].map((t) => (
                    <li key={t} className="flex items-start">
                      <CheckCircle className="text-green-700 mr-2 flex-shrink-0 mt-1" size={18} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/salle-de-bain" className="text-sm text-orange-700 underline hover:text-orange-800">
                  Prix d'une douche sénior et reste à charge
                </Link>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h3 className="text-xl font-bold text-blue-700 mb-4">
                  Circulation et accessibilité
                </h3>
                <ul className="space-y-2 mb-4">
                  {[
                    "Rampes d'accès intérieures et extérieures",
                    "Élargissement des portes",
                    "Monte-escalier",
                  ].map((t) => (
                    <li key={t} className="flex items-start">
                      <CheckCircle className="text-green-700 mr-2 flex-shrink-0 mt-1" size={18} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/monte-escalier" className="text-sm text-orange-700 underline hover:text-orange-800">
                  Prix d'un monte-escalier et aides mobilisables
                </Link>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <h3 className="text-xl font-bold text-green-700 mb-4">Extérieurs et accès</h3>
                <ul className="space-y-2 mb-4">
                  {[
                    "Chemins d'accès sécurisés",
                    "Éclairage automatique extérieur",
                    "Suppression des seuils et des marches",
                  ].map((t) => (
                    <li key={t} className="flex items-start">
                      <CheckCircle className="text-green-700 mr-2 flex-shrink-0 mt-1" size={18} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/exterieurs" className="text-sm text-orange-700 underline hover:text-orange-800">
                  Adapter les accès et les extérieurs
                </Link>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                <h3 className="text-xl font-bold text-purple-700 mb-4">Sécurité du logement</h3>
                <ul className="space-y-2 mb-4">
                  {[
                    "Volets roulants motorisés",
                    "Éclairage automatique des circulations",
                    "Adaptation des commandes et interrupteurs",
                  ].map((t) => (
                    <li key={t} className="flex items-start">
                      <CheckCircle className="text-green-700 mr-2 flex-shrink-0 mt-1" size={18} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/accessibilite" className="text-sm text-orange-700 underline hover:text-orange-800">
                  Tous les travaux d'accessibilité finançables
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ locale : alimente aussi le bloc FAQPage en données structurées */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10">Questions fréquentes {ou}</h2>
            <div className="space-y-6">
              {faq.map((q) => (
                <div key={q.question} className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{q.question}</h3>
                  <p className="text-gray-700 leading-relaxed">{q.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Requêtes locales.
          « adaptation logement personne âgée <ville> » et « aide salle de bain
          senior <département> » sont peu concurrentielles et très qualifiées :
          celui qui les tape a déjà identifié son besoin. Le contenu reprend le
          contexte de bâti décrit plus haut, du point de vue de sa recherche. */}
      {local && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Adaptation du logement pour personne âgée {ou}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">{local.adaptation}</p>
              <p className="text-gray-700 leading-relaxed mb-10">
                Nous intervenons notamment à {local.villes}.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Aide pour une salle de bain senior {ou}
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">{local.salleDeBain}</p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/simulateur"
                  onClick={() => trackSimulatorClick(`departement-${code}-local`)}
                  className="inline-flex items-center px-6 py-3 bg-orange-700 text-white font-bold rounded-lg hover:bg-orange-800 transition-colors"
                >
                  Tester mon éligibilité
                </Link>
                <Link
                  to="/salle-de-bain"
                  className="inline-flex items-center px-6 py-3 border-2 border-orange-200 text-gray-900 font-bold rounded-lg hover:bg-orange-50 transition-colors"
                >
                  Adapter ma salle de bain
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Communes : maillage local */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Nous intervenons dans tout le département</h2>
            <p className="text-gray-700 mb-6">
              Notamment à {communes.slice(0, -1).join(", ")} et {communes[communes.length - 1]}.
            </p>
            <div className="flex items-center justify-center gap-2 text-gray-700">
              <Award className="text-orange-700" size={20} />
              <span>Accompagnement AMO pris en charge dans le cadre de MaPrimeAdapt'</span>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Ils nous ont fait confiance {ou}
          </h2>
          <Testimonials />
        </div>
      </section>

      {/* Autres départements : maillage interne entre pages locales */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold mb-4">Nos autres départements d'intervention</h2>
            <div className="flex flex-wrap gap-3">
              {[
                { cle: "paris", nom: "Paris", code: "75" },
                { cle: "seine-et-marne", nom: "Seine-et-Marne", code: "77" },
                { cle: "essonne", nom: "Essonne", code: "91" },
                { cle: "hauts-de-seine", nom: "Hauts-de-Seine", code: "92" },
                { cle: "seine-saint-denis", nom: "Seine-Saint-Denis", code: "93" },
                { cle: "val-doise", nom: "Val-d'Oise", code: "95" },
              ]
                .filter((a) => a.cle !== dept)
                .map((a) => (
                  <Link
                    key={a.cle}
                    to={`/amo-anah-${a.cle}-${a.code}`}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-orange-500 hover:text-orange-700 transition-colors"
                  >
                    {a.nom} ({a.code})
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-700 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Adapter votre logement {ou} : parlons-en
          </h2>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
            Visite d'évaluation à domicile gratuite, partout {ou} ({code}).
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <Link
              to="/simulateur"
              onClick={() => trackSimulatorClick("departement_cta")}
              className="bg-white text-orange-700 text-xl font-bold px-10 py-4 rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              Test d'éligibilité gratuit
            </Link>
            <a
              href="tel:0939035444"
              onClick={() => trackPhoneClick("departement_cta")}
              className="bg-transparent border-2 border-white text-white text-xl font-bold px-10 py-4 rounded-lg hover:bg-white hover:text-orange-700 transition-colors flex items-center gap-2"
            >
              <Phone size={24} />
              09 39 03 54 44
            </a>
          </div>
          <p className="text-white mt-6 text-sm">
            Assistant à maîtrise d'ouvrage agréé ANAH — dossiers {de} suivis de bout en bout
          </p>
        </div>
      </section>

      <GuidesLies slugs={["comment-obtenir-maprimeadapt", "cumul-aides-maprimeadapt"]} />

      </main>

      <Footer />
    </div>
  );
};

export default DepartementPage;
